import dayjs from 'dayjs';

/**
 * Regras do receituário compartilhadas pelo card, pelo editor e pelo PDF.
 * Os limites espelham public/api/prescription/Prescriptions.php.
 *
 * Receita de controle especial (Portaria SVS/MS 344/98): sai em duas vias,
 * a 1ª fica retida na farmácia ou drogaria e a 2ª volta com o paciente.
 */

export const KIND_SIMPLE = 'simple';
export const KIND_SPECIAL = 'special';

export const KIND_LABELS = Object.freeze({
  [KIND_SIMPLE]: 'Receita simples',
  [KIND_SPECIAL]: 'Controle especial',
});

/** Nome do PDF da receita (buildReportFileName). */
export const PRESCRIPTION_REPORT_TYPE = 'receita';

export const MAX_COPIES = 3;
export const MIN_SPECIAL_COPIES = 2;
export const MAX_CONTENT_LENGTH = 20000;

const DATE_FORMAT = 'YYYY-MM-DD';

export const isSpecial = (prescription) => prescription && prescription.kind === KIND_SPECIAL;

export const kindLabel = (kind) => KIND_LABELS[kind] || KIND_LABELS[KIND_SIMPLE];

/** Título da receita na lista de documentos emitidos. */
export const prescriptionTitle = (prescription) => (
  isSpecial(prescription) ? 'Receita de controle especial' : 'Receita simples'
);

/** Menor número de vias aceito para o tipo. */
export const minCopies = (kind) => (kind === KIND_SPECIAL ? MIN_SPECIAL_COPIES : 1);

/** Ajusta o número de vias ao tipo (controle especial nunca sai com menos de 2). */
export const clampCopies = (kind, copies) => {
  const value = parseInt(copies, 10) || 1;
  return Math.min(MAX_COPIES, Math.max(minCopies(kind), value));
};

/** Rótulo de cada via no papel; numa receita simples de uma via não há rótulo. */
export function copyLabel(kind, index, total) {
  if (kind === KIND_SPECIAL) {
    if (index === 0) return '1ª via: retenção da farmácia ou drogaria';
    if (index === 1) return '2ª via: orientação ao paciente';
    return `${index + 1}ª via`;
  }
  return total > 1 ? `${index + 1}ª via` : '';
}

/** Data da receita: a do atendimento (nunca no futuro), senão hoje. */
export function defaultIssuedAt(appointmentDate) {
  const today = dayjs().format(DATE_FORMAT);
  if (!appointmentDate || !dayjs(appointmentDate).isValid()) return today;
  const date = dayjs(appointmentDate).format(DATE_FORMAT);
  return date > today ? today : date;
}

export const formatIssuedAt = (issuedAt) => (issuedAt ? dayjs(issuedAt).format('DD/MM/YYYY') : '');

/** Mais recentes primeiro (data da receita, depois a ordem de criação). */
export function sortPrescriptions(list) {
  return [...(Array.isArray(list) ? list : [])].sort((a, b) => {
    if (a.issuedAt !== b.issuedAt) return a.issuedAt < b.issuedAt ? 1 : -1;
    return Number(b.id) - Number(a.id);
  });
}

/** Primeira linha do texto, para a lista de receitas. */
export function firstLine(content) {
  const line = String(content || '').split('\n').find((item) => item.trim() !== '') || '';
  return line.trim();
}

/** Endereço do paciente em uma linha, a partir do cadastro. */
export function patientAddress(patient) {
  const data = patient || {};
  const street = [data.address, data.number, data.complement].filter(Boolean).join(', ');
  const city = [data.city, data.state].filter(Boolean).join(' - ');
  return [street, data.district, city].filter(Boolean).join(', ');
}
